import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Copy, Sparkles, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Promo banner that fetches the currently active coupon and surfaces a
 * copy-to-clipboard CTA. Used on the homepage and inside the cart sheet.
 */
export const CouponBanner = ({ variant = 'home' }) => {
  const [coupon, setCoupon] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/coupons/active`, { timeout: 5000 })
      .then((r) => { if (!cancelled) setCoupon(r.data?.coupon || null); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  if (!coupon) return null;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(coupon.code);
      // Persist so the checkout page auto-applies the code on next visit
      localStorage.setItem('ss_applied_coupon', coupon.code);
      setCopied(true);
      toast.success(`Code ${coupon.code} copied`);
      setTimeout(() => setCopied(false), 2200);
    } catch {
      toast.error('Copy failed — long-press to select the code');
    }
  };

  const isCart = variant === 'cart';

  return (
    <div
      data-testid="coupon-banner"
      className={
        isCart
          ? 'rounded-md border border-dashed border-[#C4B5A0] bg-[#FAF6EE] p-3 my-3'
          : 'relative w-full bg-gradient-to-r from-[#6B4E37] via-[#8B6F4E] to-[#6B4E37] text-[#FAF9F7] py-3 px-4 overflow-hidden'
      }
    >
      {!isCart && (
        <div className="pointer-events-none absolute inset-0 opacity-20" aria-hidden="true"
             style={{backgroundImage: 'radial-gradient(circle at 20% 50%, rgba(255,255,255,0.35) 0px, transparent 60px), radial-gradient(circle at 80% 50%, rgba(255,255,255,0.25) 0px, transparent 60px)'}} />
      )}

      <div className={isCart
        ? 'flex flex-col sm:flex-row sm:items-center gap-2'
        : 'relative container mx-auto flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-4 text-center'}>
        <div className={isCart ? 'flex items-start gap-2 flex-1' : 'flex items-center gap-2'}>
          <Sparkles className={isCart ? 'h-4 w-4 text-[#6B4E37] mt-0.5' : 'h-4 w-4 text-[#FAF9F7]'} />
          <div className={isCart ? 'text-left' : ''}>
            <p className={isCart
              ? 'font-montserrat text-sm text-[#2C2C2C]'
              : 'font-montserrat text-xs sm:text-sm tracking-wide'}>
              {coupon.banner_text}
            </p>
            {coupon.urgency_text && (
              <p className={isCart
                ? 'font-montserrat text-[11px] text-[#8B7D6B] mt-0.5'
                : 'font-montserrat text-[10px] sm:text-xs text-[#E8D8C5] mt-0.5 tracking-wider uppercase'}>
                {coupon.urgency_text}
              </p>
            )}
          </div>
        </div>

        <button
          type="button"
          onClick={handleCopy}
          data-testid="coupon-copy-btn"
          className={isCart
            ? 'inline-flex items-center gap-1.5 text-xs font-montserrat tracking-wider uppercase px-3 py-1.5 rounded border border-[#6B4E37] text-[#6B4E37] hover:bg-[#6B4E37] hover:text-white transition shrink-0'
            : 'inline-flex items-center gap-1.5 text-[11px] sm:text-xs font-montserrat tracking-wider uppercase px-3 py-1 rounded-full bg-[#FAF9F7] text-[#6B4E37] hover:bg-white transition shrink-0'}
        >
          {copied ? <CheckCircle2 className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
          {copied ? 'Copied' : `Copy ${coupon.code}`}
        </button>
      </div>
    </div>
  );
};
